/**
 * Associations Management Page
 * Manage HOA, Condo, and Property Management Company accounts
 * Bulk unit management, seasonal maintenance, consolidated billing
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Building2, Home, Percent, DollarSign } from 'lucide-react';

interface Association {
  id: string;
  name: string;
  association_type: string;
  unit_count: number;
  billing_email: string;
  volume_discount_pct: number;
  status: string;
  created_at: string;
}

export default function AssociationsManagement() {
  const [associations, setAssociations] = useState<Association[]>([]);
  const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadAssociations();
  }, []);

  const loadAssociations = async () => {
    setLoading(true);
    
    const { data } = await supabase
      .from('associations')
      .select('*')
      .order('name', { ascending: true });
    
    setAssociations(data || []);
    setLoading(false);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'hoa': return '🏘️';
      case 'condo': return '🏢';
      case 'pmc': return '🏗️';
      case 'commercial': return '🏪';
      default: return '🏠';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getVolumeDiscount = (unitCount: number): number => {
    if (unitCount >= 300) return 15;
    if (unitCount >= 150) return 12;
    if (unitCount >= 50) return 8;
    return 0;
  };

  return (
    <AdminPageLayout
      title="Associations Management"
      subtitle="HOA, Condo, and Property Management accounts"
      actions={
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          + New Association
        </button>
      }
    >
      {/* Summary Cards */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Associations"
          value={associations.length}
          icon={Building2}
          subtitle={`${associations.filter(a => a.status === 'active').length} active`}
        />
        <StatCard
          label="Total Units"
          value={associations.reduce((sum, a) => sum + (a.unit_count || 0), 0)}
          icon={Home}
          subtitle="under management"
        />
        <StatCard
          label="Avg Discount"
          value={`${associations.length > 0
            ? (associations.reduce((sum, a) => sum + (a.volume_discount_pct || 0), 0) / associations.length).toFixed(1)
            : '0.0'}%`}
          icon={Percent}
          subtitle="volume-based"
        />
        <StatCard
          label="This Month MRR"
          value="$0"
          icon={DollarSign}
          subtitle="monthly recurring"
        />
      </StatsGrid>

      {/* Associations List */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Associations</h2>
        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : associations.length === 0 ? (
          <div className="py-8 text-center text-gray-400">
            No associations yet. Click "New Association" to get started.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Association</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {associations.map((assoc) => (
                  <tr key={assoc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{getTypeIcon(assoc.association_type)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{assoc.name}</div>
                          <div className="text-xs text-gray-500">
                            Since {new Date(assoc.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {assoc.association_type?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{assoc.unit_count || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getVolumeDiscount(assoc.unit_count)}%
                      </div>
                      {assoc.volume_discount_pct !== getVolumeDiscount(assoc.unit_count) && (
                        <div className="text-xs text-orange-600">
                          Custom: {assoc.volume_discount_pct}%
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{assoc.billing_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(assoc.status)}`}>
                        {assoc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedAssociation(assoc)}
                        className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                      >
                        View
                      </button>
                      <button className="text-gray-600 hover:text-gray-800 font-medium">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Association Detail Modal */}
      {selectedAssociation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">{selectedAssociation.name}</h2>
              <button
                onClick={() => setSelectedAssociation(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Association Details</h3>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-600">Type</dt>
                      <dd className="font-medium">{selectedAssociation.association_type}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Units</dt>
                      <dd className="font-medium">{selectedAssociation.unit_count}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Volume Discount</dt>
                      <dd className="font-medium">{selectedAssociation.volume_discount_pct}%</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Billing Email</dt>
                      <dd className="font-medium">{selectedAssociation.billing_email}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-left">
                      📋 View Units & Buildings
                    </button>
                    <button className="w-full px-4 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-left">
                      📅 Schedule Maintenance
                    </button>
                    <button className="w-full px-4 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-left">
                      💰 Generate Invoice
                    </button>
                    <button className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-left">
                      📊 View Reports
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
}
